// Office.js integration seam. Everything host-specific lives here so the rest
// of the app is plain React and renders in a normal browser for verification.
//
// Host context is optional: in a plain browser we fall back to a mock "current
// email" so the flows stay demoable. Those fallbacks are gated on inOutlook() —
// inside Outlook we never invent an item, because the pane can be launched from
// the top-level toolbar with nothing selected.
//
// SSO getAccessToken, the ItemChanged subscription and displayReplyForm are all
// implemented below (the old "phase 2-4 TODO" note here was stale).

export interface OfficeState {
  /** True when running inside an Outlook host (vs. a plain browser). */
  inHost: boolean;
}

/** Is the Outlook host live RIGHT NOW?
 *
 *  Deliberately a function, not a latched boolean. It used to be
 *  `let hostReady = false`, set once from Office.onReady and then read by nine
 *  call sites. That made a startup race permanently disabling: if the flag was
 *  read before (or latched without) readiness, SSO, ItemChanged,
 *  displayReplyForm and the item accessors all stayed dead for the WHOLE session,
 *  and the pane silently ran in browser-fallback mode inside real Outlook. That is
 *  exactly the "works sometimes, not others" symptom — a cold pane launch from the
 *  top-level toolbar loses the race more often than a warm in-message launch.
 *
 *  Computing it from live state means a slow host simply works as soon as it's up,
 *  and no single mis-timed read can poison the session. */
function hostAvailable(): boolean {
  const Office = (globalThis as any).Office;
  return !!Office?.context?.mailbox;
}

/** Public read of the same live state, for the "this only runs inside Outlook"
 *  gate in App.tsx. Same reasoning as above: a function, never a cached boolean. */
export function isInOutlook(): boolean {
  return hostAvailable();
}

/** Longest we'll wait for the host before treating this as a plain browser. */
const HOST_READY_TIMEOUT_MS = 8000;
/** How often to re-check while waiting. Office.onReady is the primary signal;
 *  polling is the belt-and-braces one, because a missed/late onReady must not be
 *  able to strand the pane. */
const HOST_POLL_MS = 250;

const lateReadyCallbacks: (() => void)[] = [];
let watching = false;

/** Run cb once the host is available — immediately if it already is, otherwise as
 *  soon as it shows up (even long after initOffice() resolved). */
export function onHostReady(cb: () => void): void {
  if (hostAvailable()) {
    cb();
    return;
  }
  lateReadyCallbacks.push(cb);
  watchForHost();
}

/** Poll until the host appears, then flush the waiting callbacks. Idempotent. */
function watchForHost(): void {
  if (watching) return;
  watching = true;
  const startedAt = Date.now();
  const timer = setInterval(() => {
    const up = hostAvailable();
    if (!up && Date.now() - startedAt < HOST_READY_TIMEOUT_MS) return;
    clearInterval(timer);
    watching = false;
    if (up) {
      console.info(`[office] host became available after ${Date.now() - startedAt}ms — re-syncing`);
      for (const cb of lateReadyCallbacks.splice(0)) cb();
    } else {
      console.info("[office] no Outlook host detected — running in browser mode");
      lateReadyCallbacks.length = 0;
    }
  }, HOST_POLL_MS);
}

/** Resolve once we know whether we're in a host. Safe to call more than once
 *  (React StrictMode double-invokes effects in development, and the previous
 *  implementation left the second call's promise pending forever). */
export function initOffice(): Promise<OfficeState> {
  if (hostAvailable()) return Promise.resolve({ inHost: true });

  const OfficeGlobal = (globalThis as any).Office;
  // Plain browser with no Office.js at all: don't make local dev wait out the
  // backstop before the pane syncs.
  if (!OfficeGlobal) return Promise.resolve({ inHost: false });

  return new Promise((resolve) => {
    const Office = OfficeGlobal;
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      clearInterval(timer);
      const inHost = hostAvailable();
      console.info(`[office] init complete — inHost=${inHost}`);
      resolve({ inHost });
    };

    // Primary signal.
    Office?.onReady?.(() => finish());

    // Backstop + poll: covers a missing/late onReady and a genuinely non-host page.
    const startedAt = Date.now();
    const timer = setInterval(() => {
      if (hostAvailable() || Date.now() - startedAt >= HOST_READY_TIMEOUT_MS) finish();
    }, HOST_POLL_MS);
  });
}

/** True when we're really running inside Outlook (not a plain browser).
 *
 *  This gates the demo fallbacks below. Getting it wrong is not cosmetic: the
 *  add-in can be launched from the top-level Outlook toolbar with NO message
 *  selected (SupportsNoItemContext), and the fallbacks used to fire in that
 *  case too — so the pane claimed the user was reading "PTO request for the
 *  first week of August" from "Dana Williams", and the backend then hydrated
 *  that fake id from mock data and summarized an email that does not exist in
 *  the user's mailbox. In a host we must report "no message" honestly. */
function inOutlook(): boolean {
  return hostAvailable();
}

/** The message the user is reading, or null when nothing is selected.
 *  Outside a host, falls back to the demo scenario so flows stay demoable. */
export function getCurrentEmailId(): string | null {
  const Office = (globalThis as any).Office;
  const item = Office?.context?.mailbox?.item;
  if (item?.itemId) return item.itemId as string;
  if (inOutlook()) return null; // in Outlook with no item selected — say so
  return "pto-dana"; // browser/dev fallback only
}

export interface EmailMeta {
  id: string | null;
  subject: string;
  fromName: string;
}

/** Readable metadata for the context strip (sync; no body fetch). */
export function getCurrentEmailMeta(): EmailMeta {
  const Office = (globalThis as any).Office;
  const item = Office?.context?.mailbox?.item;
  if (item) {
    return {
      id: (item.itemId as string) ?? null,
      subject: (item.subject as string) || "(no subject)",
      fromName: item.from?.displayName || item.from?.emailAddress || "",
    };
  }
  // In Outlook with nothing selected: empty subject drives the pane's existing
  // "No message selected" state. Policy questions still work; email-specific
  // actions correctly have nothing to act on.
  if (inOutlook()) return { id: null, subject: "", fromName: "" };
  // Browser/dev fallback only.
  return { id: "pto-dana", subject: "PTO request for the first week of August", fromName: "Dana Williams" };
}

// One Office handler, fanned out to N subscribers.
//
// The previous version called addHandlerAsync directly and bailed silently when
// hostAvailable() was false at that instant — so on a slow launch the pinned pane
// simply never learned about item changes and showed a stale email forever. It
// also had no dedup (React StrictMode mounts effects twice, giving two handlers
// and two "Now viewing" lines) and no way to unsubscribe.
const itemChangedCallbacks = new Set<() => void>();
let officeHandlerBound = false;

/** Fire cb when the user selects a different message (pinned pane). Safe to call
 *  before the host is ready — binding is deferred until it is. Returns an
 *  unsubscribe function. */
export function onItemChanged(cb: () => void): () => void {
  itemChangedCallbacks.add(cb);
  bindItemChanged();
  return () => itemChangedCallbacks.delete(cb);
}

function bindItemChanged(): void {
  if (officeHandlerBound) return;
  const bind = () => {
    if (officeHandlerBound) return;
    const Office = (globalThis as any).Office;
    const mailbox = Office?.context?.mailbox;
    if (!mailbox?.addHandlerAsync || !Office?.EventType?.ItemChanged) return;
    officeHandlerBound = true;
    mailbox.addHandlerAsync(Office.EventType.ItemChanged, () => {
      // Recompute whether we're in the shared mailbox BEFORE the subscribers run:
      // the answer belongs to the item, so it is stale the instant the item changes,
      // and a stale value would attribute shared-mailbox turns to a personal one.
      refreshMailboxContext();
      for (const cb of [...itemChangedCallbacks]) cb();
    });
    // And once for the item the pane opened on, which fires no ItemChanged.
    refreshMailboxContext();
    console.info("[office] subscribed to ItemChanged");
  };
  if (hostAvailable()) bind();
  else onHostReady(bind);
}

/** A message from a multi-message selection. Shape mirrors what
 *  getSelectedItemsAsync returns (requirement set 1.13). */
export interface SelectedMessage {
  itemId: string;
  conversationId: string | null;
  /** Stable across mailboxes — this is what lets the backend find the same
   *  message inside the HR shared mailbox. */
  internetMessageId: string | null;
  subject: string;
  itemType: string | null;
  itemMode: string | null;
}

/** Messages the user has selected in the list.
 *
 *  In multi-select mode `Office.context.mailbox.item` is NULL — the single-item
 *  accessors above deliberately report "no message" there, and this is how you get
 *  the actual selection instead. Returns [] when unsupported or nothing usable is
 *  selected, so callers can treat it as "no multi-selection".
 *
 *  Requires Mailbox 1.13 (same set as SupportsNoItemContext), feature-detected. */
export function getSelectedMessages(): Promise<SelectedMessage[]> {
  const Office = (globalThis as any).Office;
  const mailbox = Office?.context?.mailbox;
  if (!hostAvailable() || typeof mailbox?.getSelectedItemsAsync !== "function") {
    return Promise.resolve([]);
  }
  return new Promise((resolve) => {
    try {
      mailbox.getSelectedItemsAsync((res: any) => {
        if (res?.status === "failed" || !Array.isArray(res?.value)) {
          console.warn("[office] getSelectedItemsAsync failed:", res?.error?.message);
          resolve([]);
          return;
        }
        resolve(
          res.value.map((m: any) => ({
            itemId: String(m?.itemId ?? ""),
            conversationId: m?.conversationId ?? null,
            internetMessageId: m?.internetMessageId ?? null,
            subject: m?.subject ?? "",
            itemType: m?.itemType ?? null,
            itemMode: m?.itemMode ?? null,
          })),
        );
      });
    } catch (err) {
      console.warn("[office] getSelectedItemsAsync threw:", err);
      resolve([]);
    }
  });
}

// SelectedItemsChanged — fires as the user adds/removes messages from the
// selection while the pane is open. Same fan-out + deferred-binding shape as
// ItemChanged and OfficeThemeChanged.
const selectionChangedCallbacks = new Set<() => void>();
let selectionHandlerBound = false;

/** Fire cb when the multi-message selection changes. Returns an unsubscribe.
 *  Safe to call before the host is ready; no-ops where unsupported. */
export function onSelectedItemsChanged(cb: () => void): () => void {
  selectionChangedCallbacks.add(cb);
  bindSelectionChanged();
  return () => selectionChangedCallbacks.delete(cb);
}

function bindSelectionChanged(): void {
  if (selectionHandlerBound) return;
  const bind = () => {
    if (selectionHandlerBound) return;
    const Office = (globalThis as any).Office;
    const mailbox = Office?.context?.mailbox;
    const evt = Office?.EventType?.SelectedItemsChanged;
    if (!mailbox?.addHandlerAsync || !evt) return; // unsupported on this host
    selectionHandlerBound = true;
    mailbox.addHandlerAsync(evt, () => {
      for (const cb of [...selectionChangedCallbacks]) cb();
    });
    console.info("[office] subscribed to SelectedItemsChanged");
  };
  if (hostAvailable()) bind();
  else onHostReady(bind);
}

/** Outlook's own theme, as "dark" | "light", or null when the host doesn't expose it.
 *
 *  Office.context.officeTheme is the authoritative signal and the reason we don't
 *  just use CSS prefers-color-scheme: Outlook has its OWN dark-mode toggle that is
 *  independent of the OS/browser setting, so the two routinely disagree. The pane
 *  is an iframe with its own document, so prefers-color-scheme reports the
 *  OS preference and would leave the pane light inside a dark Outlook.
 *
 *  Rather than trust a theme *name* (the values differ across hosts and versions),
 *  derive it from the background colour Outlook hands us. */
export function getOfficeThemeMode(): "dark" | "light" | null {
  const Office = (globalThis as any).Office;
  const theme = Office?.context?.officeTheme;
  const bg: unknown = theme?.bodyBackgroundColor;
  if (typeof bg !== "string" || !bg) return null;
  const lum = relativeLuminance(bg);
  if (lum === null) return null;
  return lum < 0.5 ? "dark" : "light";
}

/** Perceived luminance (0 = black, 1 = white) of "#rrggbb", "#rgb" or "rgb(r,g,b)". */
function relativeLuminance(color: string): number | null {
  const c = color.trim();
  let r: number;
  let g: number;
  let b: number;

  const hex = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(c);
  if (hex) {
    const h = hex[1];
    if (h.length === 3) {
      r = parseInt(h[0] + h[0], 16);
      g = parseInt(h[1] + h[1], 16);
      b = parseInt(h[2] + h[2], 16);
    } else {
      r = parseInt(h.slice(0, 2), 16);
      g = parseInt(h.slice(2, 4), 16);
      b = parseInt(h.slice(4, 6), 16);
    }
  } else {
    const rgb = /^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i.exec(c);
    if (!rgb) return null;
    r = Number(rgb[1]);
    g = Number(rgb[2]);
    b = Number(rgb[3]);
  }
  if ([r, g, b].some((v) => !Number.isFinite(v))) return null;
  // Rec. 601 luma — enough to classify light vs dark, and cheap.
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255;
}

// Outlook can change theme while the pane is open. Same fan-out/deferred-binding
// shape as ItemChanged, for the same reason: binding must not be lost to a race.
const themeChangedCallbacks = new Set<() => void>();
let themeHandlerBound = false;

/** Fire cb when Outlook's theme changes. Returns an unsubscribe. Safe to call
 *  before the host is ready; no-ops on hosts without OfficeThemeChanged. */
export function onOfficeThemeChanged(cb: () => void): () => void {
  themeChangedCallbacks.add(cb);
  bindThemeChanged();
  return () => themeChangedCallbacks.delete(cb);
}

function bindThemeChanged(): void {
  if (themeHandlerBound) return;
  const bind = () => {
    if (themeHandlerBound) return;
    const Office = (globalThis as any).Office;
    const mailbox = Office?.context?.mailbox;
    const evt = Office?.EventType?.OfficeThemeChanged;
    if (!mailbox?.addHandlerAsync || !evt) return; // not supported on this host
    themeHandlerBound = true;
    mailbox.addHandlerAsync(evt, () => {
      for (const cb of [...themeChangedCallbacks]) cb();
    });
    console.info("[office] subscribed to OfficeThemeChanged");
  };
  if (hostAvailable()) bind();
  else onHostReady(bind);
}

/** Test seam: reset module state between harness cases. Not used by the app. */
export function __resetOfficeStateForTests(): void {
  itemChangedCallbacks.clear();
  officeHandlerBound = false;
  themeChangedCallbacks.clear();
  themeHandlerBound = false;
  selectionChangedCallbacks.clear();
  selectionHandlerBound = false;
  lateReadyCallbacks.length = 0;
  watching = false;
}

/** The current item id in Graph REST format (what /api/mail expects).
 *  Office item ids are EWS-format; convertToRestId translates client-side. */
export function getRestItemId(): string | null {
  const Office = (globalThis as any).Office;
  const mailbox = Office?.context?.mailbox;
  const itemId = mailbox?.item?.itemId;
  if (!hostAvailable() || !itemId) return null;
  try {
    return mailbox.convertToRestId(itemId, Office.MailboxEnums.RestVersion.v2_0) as string;
  } catch {
    return itemId as string;
  }
}

/** The RFC-5322 Internet message id of the open message (e.g.
 *  "<abc123@contoso.com>"). Unlike the Office/EWS item id, this is identical on
 *  every copy of the email, so the backend can resolve the same message inside
 *  the HR shared mailbox regardless of which mailbox the user is reading from. */
export function getInternetMessageId(): string | null {
  const Office = (globalThis as any).Office;
  const item = Office?.context?.mailbox?.item;
  if (!hostAvailable() || !item) return null;
  const id = item.internetMessageId;
  return typeof id === "string" && id ? id : null;
}

/** Open a URL (e.g. a SharePoint document) in the browser from the pane. */
export function openUrl(url: string): void {
  const Office = (globalThis as any).Office;
  if (hostAvailable() && Office?.context?.ui?.openBrowserWindow) {
    Office.context.ui.openBrowserWindow(url);
  } else {
    window.open(url, "_blank", "noopener");
  }
}

export interface EmailContext {
  subject: string;
  from: string;
  body: string;
}

/** Read the currently-open message's real content (subject/from/body) from
 *  Office.js so the model works on the actual email. Returns null in a browser,
 *  where the backend falls back to mock content keyed by currentEmailId. */
export async function getCurrentEmail(): Promise<EmailContext | null> {
  const Office = (globalThis as any).Office;
  const item = Office?.context?.mailbox?.item;
  if (!hostAvailable() || !item?.body?.getAsync) return null;

  const subject: string = item.subject ?? "";
  const from: string = item.from ? `${item.from.displayName ?? ""} <${item.from.emailAddress ?? ""}>`.trim() : "";
  const body: string = await new Promise((resolve) => {
    item.body.getAsync("text", (res: any) => resolve(res?.status === "succeeded" ? (res.value as string) : ""));
  });
  return { subject, from, body };
}

/** Prefill Outlook's native reply editor with the approved draft — the
 *  "Open in Outlook editor" half of the hybrid send model. */
/** Plain draft text -> the htmlBody Office.js wants.
 *
 *  Both compose paths used to pass `text.replace(/\n/g, "<br>")` straight through,
 *  which treats the draft as HTML while the pane's preview treats it as plain text.
 *  Anything markup-shaped in the draft therefore rendered ONE way in the preview the
 *  user approves and ANOTHER way in the email that goes out — the mechanism behind a
 *  malformed link in a generated draft. An ordinary sentence was enough to break it:
 *  "salary < $50k" makes the rest of the paragraph a bogus tag.
 *
 *  The backend already strips link markup from drafts (normalizeDraftText). This is
 *  the second half of the same fix, and the one that matters even for text no model
 *  wrote — a user can edit the draft, and `&` must survive as `&`. */
export function draftToHtmlBody(text: string): string {
  return String(text)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/\n/g, "<br>");
}

export function displayReplyForm(text: string): boolean {
  const Office = (globalThis as any).Office;
  const item = Office?.context?.mailbox?.item;
  if (hostAvailable() && item?.displayReplyForm) {
    item.displayReplyForm({ htmlBody: draftToHtmlBody(text) });
    return true;
  }
  return false; // not in a host; caller shows a toast instead
}

/** Open Outlook's native NEW-message compose window prefilled with a
 *  model-drafted subject + body (the "draft from scratch" flow). */
export function displayNewMessageForm(subject: string, text: string): boolean {
  const Office = (globalThis as any).Office;
  const mailbox = Office?.context?.mailbox;
  if (hostAvailable() && mailbox?.displayNewMessageForm) {
    mailbox.displayNewMessageForm({
      subject: subject || "",
      htmlBody: draftToHtmlBody(text),
    });
    return true;
  }
  return false; // not in a host; caller shows a toast instead
}

/** The Office SSO error code from the most recent getAccessToken() attempt, or
 *  null if the last attempt succeeded (or SSO isn't available at all).
 *
 *  Exposed so the pane can say something TRUE about why a 401 happened. Every
 *  cause otherwise renders as the same "Something went wrong" bubble, which is how
 *  "not assigned to the Entra app" (13005-class) was reported as "the assistant is
 *  not responding". The console line is not enough — nobody opens devtools in a
 *  task pane during a demo. */
let lastSsoError: number | null = null;
export function getLastSsoError(): number | null {
  return lastSsoError;
}

/** Entra SSO token for the backend. Returns null when SSO is unavailable or
 *  fails, in which case client.ts omits the Authorization header and the backend
 *  answers 401 missing_token (when REQUIRE_AUTH=true). */
/** The token request currently in flight, if any.
 *
 *  Office.js allows ONE outstanding getAccessToken call per add-in. A second
 *  concurrent call fails with **13008** — "this add-in is already requesting an
 *  access token" — and we were causing that ourselves: authHeaders() calls this on
 *  every request, and the pane fires several at mount (the history probe, the
 *  conversation load). Seen live in an Outlook web console 2026-08-10, immediately
 *  followed by `/api/conversations 401`.
 *
 *  So: single-flight. Concurrent callers share one request and one answer. Not a
 *  token CACHE — Office.js already caches and handles expiry, and duplicating that
 *  here would mean serving a stale token past its lifetime. This only collapses
 *  calls that overlap in time. */
let inFlightToken: Promise<string | null> | null = null;

export function getAccessToken(): Promise<string | null> {
  if (inFlightToken) return inFlightToken;
  inFlightToken = acquireAccessToken().finally(() => {
    inFlightToken = null;
  });
  return inFlightToken;
}

async function acquireAccessToken(): Promise<string | null> {
  const OfficeRuntime = (globalThis as any).OfficeRuntime;
  // The API object can exist while Office.js is still wiring itself up, and calling
  // it then throws `Cannot read properties of undefined (reading
  // 'getAccessTokenAsync')` from inside Office's own code — which surfaces as a bare
  // 401 with no error code, the least diagnosable shape this failure has. Waiting
  // for the mailbox context avoids it; callers already retry on NotAuthenticatedYet.
  if (!hostAvailable() || !OfficeRuntime?.auth?.getAccessToken) {
    console.warn("[sso] getAccessToken unavailable", { inHost: hostAvailable(), hasApi: !!OfficeRuntime?.auth?.getAccessToken });
    lastSsoError = null;
    return null;
  }
  try {
    // allowConsentPrompt matters as much as allowSignInPrompt and was missing.
    // Without it, the FIRST user who has never consented to `access_as_user` gets
    // 13005/13002 and no token — Office won't offer the consent dialog unless
    // asked. The user is signed in, so allowSignInPrompt alone changes nothing,
    // and the pane just looks broken. Safe to request: the scope is User-consent
    // ("Access BayMark HR Assistant as you"), not an admin-only permission.
    const token = await OfficeRuntime.auth.getAccessToken({ allowSignInPrompt: true, allowConsentPrompt: true });
    lastSsoError = null;
    return token;
  } catch (err: any) {
    lastSsoError = typeof err?.code === "number" ? err.code : null;
    // Log err.code — this used to be a bare `catch { return null }`, which threw
    // away the ONLY piece of information that distinguishes these causes. Every
    // one of them otherwise presents identically as "chat failed: 401".
    //   13000 getAccessToken unsupported by this Outlook build
    //   13001 user not signed in to Office   13002 consent prompt aborted
    //   13003 unsupported user type (MSA/anonymous)  13005 grant not consented
    //   13006 / 13008 / 13010 transient / in-progress / unsupported iframe
    //   13012 unsupported context (often localhost or a tunnel)
    console.error("[sso] getAccessToken failed — code:", err?.code, "message:", err?.message ?? err);
    return null;
  }
}

/* --------------------------------------------------------------------------
   Turn context, for analytics only. Nothing here is authorized on, and nothing
   here changes an answer — the backend normalizes both values to a closed set
   before they reach the database (routes/chat.ts).
   -------------------------------------------------------------------------- */

/** Which Outlook this is: OutlookWebApp, OutlookWindows, OutlookMac, …
 *
 *  `Office.context.diagnostics` was added in requirement set 1.5, which the
 *  manifest already requires, but this stays defensive: the pane also renders in a
 *  plain browser for local review, where none of it exists. */
export function getClientPlatform(): string | null {
  const Office = (globalThis as any).Office;
  const host = Office?.context?.diagnostics?.hostName;
  return typeof host === "string" && host ? host : null;
}

/** Cached result of the shared-mailbox probe.
 *
 *  `getSharedPropertiesAsync` is an async round trip to the host, and the answer
 *  cannot change without the item changing — so this is recomputed on item change
 *  (below) rather than on every turn, which would put a host call on the critical
 *  path of every message the user sends. */
let mailboxContext: "shared" | "own" | null = null;

export function getMailboxContext(): "shared" | "own" | null {
  return mailboxContext;
}



/** Work out whether the open item lives in the HR SHARED mailbox or the user's own.
 *
 *  This is the only way to answer "is anyone actually using the add-in in the shared
 *  mailbox", which is the question the whole SupportsSharedFolders rollout turns on.
 *
 *  `getSharedPropertiesAsync` FAILS — rather than returning a flag — when the item is
 *  in the user's own mailbox. That is the documented contract, not an error worth
 *  logging: Microsoft's own sample treats a failed result as "not shared". So a
 *  failure maps to "own", and only a genuine absence of host or item leaves it null.
 *
 *  Requires <SupportsSharedFolders>true</SupportsSharedFolders> in the manifest
 *  (added in 1.1.4) — without it the API is not merely unavailable, the add-in never
 *  activates in a shared mailbox at all, so this would never be called there. */
export function refreshMailboxContext(): void {
  const Office = (globalThis as any).Office;
  const item = Office?.context?.mailbox?.item;
  if (!hostAvailable() || !item?.getSharedPropertiesAsync) {
    mailboxContext = null;
    return;
  }
  try {
    item.getSharedPropertiesAsync((result: any) => {
      const failed = result?.status === "failed" || result?.status === Office?.AsyncResultStatus?.Failed;
      mailboxContext = failed || !result?.value ? "own" : "shared";
    });
  } catch {
    // A host that throws synchronously tells us nothing either way. Null, not "own":
    // guessing here would quietly report shared-mailbox usage as personal.
    mailboxContext = null;
  }
}
